/**
 * PRIV-01/PRIV-02 — destek modalı PII maskeleme (Faz 1 Plan 01 + Plan 02).
 *
 * `sensei-extension/src/blackbox/redact.ts`'ten PORT EDİLDİ (D-01), import
 * EDİLMEDİ: `frontend` ve `sensei-extension` ayrı npm workspace paketleri ve
 * aralarında paylaşılan `packages/` yok (bkz. 01-CONTEXT.md D-01). Amazon
 * sayfa bağlamına özgü kısımlar (buildPageContext, buildHtmlSnippet, ASIN,
 * selamlama-isim, teslimat-adresi) BİLEREK port edilmedi — web destek akışının
 * ihtiyacı değil.
 *
 * TEK YÜRÜTME DİZİSİ sözleşmesi (cycle-3 HIGH #1'in kapandığı yer — pazarlık
 * edilemez, bkz. 01-01-PLAN.md interface_contract): redaksiyon boru hattında
 * yürütülen HER adım `REDACTION_RULES` dizisinin ÜYESİDİR. `redactValue` bu
 * dizi üzerinde SAF bir katlamadır; dizinin dışında yürütülen, digest'e
 * görünmeyen bir adım YOKTUR. Yeni bir kural eklerken:
 *   1. Bu diziye yeni `id` + `label` ile bir kayıt ekle (textRule/structuralRule).
 *   2. AYNI `id` + `label` ile AYNI konuma Python `REDACTION_RULES`'a ekle.
 *   3. Fixture `text_cases` (veya `url_cases`) girdi/çıktı vakası ekle.
 *   4. `python3 scripts/ci/print-redaction-digest.py` çalıştır, sonucu
 *      fixture `rule_set_digest` alanına yaz.
 * Adım atlanırsa iki suite de kırmızı kalır; tek taraflı yapılırsa diğer taraf
 * kırmızı kalır — kapı budur.
 *
 * Regex disiplini (Task 3 (c)): kural dizisindeki hiçbir desende iç içe
 * niceleyici (`(X+)+`, `(X*)*`) veya çakışan alternatif dal YOKTUR. Yeni
 * kural eklerken bu kısıtı koru — aksi hâlde düşmanca girdi ReDoS'a yol açar.
 */

/** Fixture ile modülün eş sürümde olduğunu doğrulayan ucuz tripwire. */
export const REDACTION_FIXTURE_VERSION = 2

/**
 * `redactFeedbackMetadata` yalnız bu anahtarları dolaşır (ALLOWLIST).
 * `summary` / `description` KASITLI OLARAK burada YOK (D-11 — kullanıcının
 * yazdığı metin maskelenmez, teşhis değeri korunur). `reporter` / `contact` /
 * `email` de burada YOK — bildirenin kendi kimliği, yanıt yolu bozulmasın diye
 * dokunulmaz kalır (RESEARCH Pitfall 1).
 */
export const REDACTED_METADATA_KEYS = ['consoleLogs', 'networkLogs', 'diagnostics'] as const

/**
 * Değer iki türden biridir: 'text' (konsol mesajı, serbest metin) veya 'url'
 * (anahtar adı `url` ya da `referrer` olan alanlar). Yapısal URL adımları
 * yalnız kind === 'url' olduğunda değeri değiştirir.
 */
export type RedactionValueKind = 'text' | 'url'

export interface RedactionRule {
  id: string
  label: string
  pattern?: RegExp
  apply: (value: string, kind: RedactionValueKind) => string
}

type TextRuleReplacer = string | ((substring: string, ...args: unknown[]) => string)

/**
 * Metin kuralı fabrikası. `apply`'ı `pattern` + `replace`'ten TÜRETİR; bu
 * yüzden desen ile uygulanan işlem yapısal olarak ayrışamaz.
 */
function textRule(opts: {
  id: string
  label: string
  pattern: RegExp
  replace: TextRuleReplacer
}): RedactionRule {
  const { id, label, pattern, replace } = opts
  return {
    id,
    label,
    pattern,
    apply: (value: string) => value.replace(pattern, replace as never),
  }
}

/**
 * Yapısal adım fabrikası. `pattern` TAŞIMAZ — saf dize işlemi yapan adımlar
 * (URL kapısı, URL query temizliği, normalizasyon) bu fabrikayı kullanır.
 */
function structuralRule(opts: {
  id: string
  label: string
  apply: (value: string, kind: RedactionValueKind) => string
}): RedactionRule {
  const { id, label, apply } = opts
  return { id, label, apply }
}

// ---------------------------------------------------------------------------
// Unicode normalizasyonu (kural 0) — görünmez karakter / tam-genişlik atlatması
// ---------------------------------------------------------------------------

// Görünmez karakterler: maskeyi bölerek atlatmaya ve metnin yönünü çevirerek
// (U+202E) okunanla gönderileni ayırmaya yarar. Maskeden ÖNCE silinir.
// \u ESCAPE'LERİ ile yazılır — kaynak dosyada ham kontrol/görünmez karakter
// BULUNMAZ (critical_notes #10).
// `extension_report.py:218-236`'dan öğrenildi (kopyalanmadı — o modül farklı
// etiket seti kullanıyor).
const INVISIBLE_RE =
  /(?:\u00ad|\u034f|\u061c|\u115f|\u1160|\u17b4|\u17b5|\u180b|\u180c|\u180d|\u180e|\u200b|\u200c|\u200d|\u200e|\u200f|\u202a|\u202b|\u202c|\u202d|\u202e|\u2060|\u2061|\u2062|\u2063|\u2064|\u2066|\u2067|\u2068|\u2069|\u206a|\u206b|\u206c|\u206d|\u206e|\u206f|\u3164|\ufeff|\uffa0)/gu

// ---------------------------------------------------------------------------
// Auth başlığı (kural 1)
// ---------------------------------------------------------------------------

// Başlık adları büyük/küçük harf duyarsız; ayraç isteğe bağlı boşluk + isteğe
// bağlı tırnak + (: veya =) + isteğe bağlı boşluk + isteğe bağlı tırnak;
// değer TEK MANTIKSAL SATIRLA sınırlı — satır sonu, tırnak, virgül, kapanış
// süslü/köşeli parantez görünce durur (01-02-PLAN.md Task 1 action).
const AUTH_HEADER_RE =
  /\b(?:Authorization|Proxy-Authorization|X-Api-Key|X-Auth-Token|Cookie|Set-Cookie)[ \t]*["']?[:=][ \t]?["']?[^\r\n"',}\]]*/gi

// ---------------------------------------------------------------------------
// JWT (kural 2) — tek modül sabiti, url-encoded-secret gölge taramasıyla
// paylaşılır (Task 2 (b)).
// ---------------------------------------------------------------------------

export const JWT_RE = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g

// ---------------------------------------------------------------------------
// anahtar=değer sırları (kural 3)
// ---------------------------------------------------------------------------

// Grup 1: anahtar + ayraç (aynen korunur). Grup 2: değer (yalnız bu maskelenir).
const KEY_VALUE_SECRET_RE =
  /(\b(?:token|access[_-]?token|id[_-]?token|refresh[_-]?token|api_key|apikey|api-key|secret|password)[ \t]*["']?[ \t]*[:=][ \t]?["']?)(?!\[token\])([^\s"',}\]]*)/gi

function replaceKeyValueSecret(_match: string, prefix: string, value: string): string {
  if (value.length === 0) return prefix
  return `${prefix}[token]`
}

// ---------------------------------------------------------------------------
// Sipariş numarası (kural 4)
// ---------------------------------------------------------------------------

const ORDER_RE = /\b\d{3}-\d{7}-\d{7}\b/g

// ---------------------------------------------------------------------------
// Kart benzeri sayı dizisi (kural 5)
// ---------------------------------------------------------------------------

const CARD_RE = /\b\d{4}[ -]?\d{4}[ -]?\d{4}[ -]?\d{4}\b/g

function replaceCard(candidate: string): string {
  const digits = candidate.replace(/\D/g, '')
  // Desen zaten 4x4 hane zorunlu kılıyor; bu kontrol savunma amaçlı ikinci
  // katman (telefon/kart kurallarının KARAR fonksiyonu olması gerekliliği,
  // Task 1 acceptance).
  if (digits.length !== 16) return candidate
  return '[card]'
}

// ---------------------------------------------------------------------------
// E-posta (kural 6) — Plan 01'den, sıra Task 1'de buraya taşındı.
// ---------------------------------------------------------------------------

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g

// ---------------------------------------------------------------------------
// Telefon (kural 7) — sınır koruması yakalama grubu + lookahead ile (lookbehind
// YASAK). Grup 1: sol sınır karakteri (varsa, olduğu gibi korunur). Grup 2:
// aday rakam dizisi (karar fonksiyonuna gider). Sağ sınır lookahead ile
// (zero-width, tüketilmez — lookahead lookbehind DEĞİLDİR, yasak değil).
// ---------------------------------------------------------------------------

const PHONE_CANDIDATE_RE = /(^|[^\w.-])(\+?\d[\d ().-]{7,}\d)(?=[^\w.-]|$)/g

const DATE_LIKE_RE = /^(?:\d{4}[-./]\d{1,2}[-./]\d{1,2}|\d{1,2}[-./]\d{1,2}[-./]\d{4})/
const IPV4_RE = /^\d{1,3}(?:\.\d{1,3}){3}$/
const VERSION_RE = /^\d{1,4}(?:\.\d{1,4}){2,}$/
const VERSION_MAX_DIGITS = 8
const PHONE_MIN_DIGITS = 9
const PHONE_MAX_DIGITS = 15
const PHONE_MIN_DIGITS_WITH_PLUS = 7

/**
 * Aday telefon mu? Hane sayısı + tarih/saat/IPv4/sürüm elemesiyle karar ver.
 * `extension_report.py:150-175`'teki ölçülmüş prod bug'larından öğrenildi
 * (kopyalanmadı — o modül farklı etiket seti kullanıyor).
 */
function decidePhone(candidate: string): string {
  if (DATE_LIKE_RE.test(candidate) || IPV4_RE.test(candidate)) return candidate
  const digits = candidate.replace(/\D/g, '')
  if (VERSION_RE.test(candidate) && digits.length <= VERSION_MAX_DIGITS) return candidate
  const minimum = candidate.startsWith('+') ? PHONE_MIN_DIGITS_WITH_PLUS : PHONE_MIN_DIGITS
  if (digits.length >= minimum && digits.length <= PHONE_MAX_DIGITS) return '[phone]'
  return candidate
}

function replacePhone(_match: string, boundary: string, candidate: string): string {
  return boundary + decidePhone(candidate)
}

// ---------------------------------------------------------------------------
// URL yapisal adimlari -- ayrıştırıcı ve hazır yüzde çözücü kullanmaz.
// ---------------------------------------------------------------------------

const URL_AUTHORITY_RE =
  /^(?:[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.?|\[[0-9A-Fa-f:.]+\])(?::\d+)?$/
const HEX_PAIR_RE = /^[0-9A-Fa-f]{2}$/
const EMBEDDED_URL_RE =
  /(^|[^A-Za-z0-9._~/-])((?:https?:\/\/|\/|\?|[A-Za-z0-9._~-]+\/|[A-Za-z0-9._~-]+\?)[^\s"'`,}\])>]+)/gi

function absoluteHttpParts(value: string): { authorityEnd: number } | null {
  const schemeEnd = value.indexOf('://')
  if (schemeEnd < 0 || !/^https?$/i.test(value.slice(0, schemeEnd))) return null
  const authorityStart = schemeEnd + 3
  let authorityEnd = value.length
  for (const separator of ['/', '?', '#']) {
    const index = value.indexOf(separator, authorityStart)
    if (index >= 0 && index < authorityEnd) authorityEnd = index
  }
  const authority = value.slice(authorityStart, authorityEnd)
  if (!authority || authority.includes('@') || !URL_AUTHORITY_RE.test(authority)) return null
  return { authorityEnd }
}

function isAcceptableUrl(value: string): boolean {
  if (value === '') return true
  const hasAsciiControl = Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0
    return codePoint <= 0x20 || codePoint === 0x7f
  })
  if (hasAsciiControl || value.includes('\\')) return false

  if (value.startsWith('blob:')) {
    const inner = value.slice(5)
    const parts = absoluteHttpParts(inner)
    if (!parts || inner[parts.authorityEnd] !== '/') return false
    const opaqueId = inner.slice(parts.authorityEnd + 1)
    return opaqueId.length > 0 && !opaqueId.includes('?') && !opaqueId.includes('#')
  }

  if (absoluteHttpParts(value)) return true
  if (value.startsWith('//')) return false
  if (value.startsWith('/') || value.startsWith('?')) return true

  const firstSeparator = [...['/', '?', '#']]
    .map((separator) => value.indexOf(separator))
    .filter((index) => index >= 0)
    .reduce((earliest, index) => Math.min(earliest, index), value.length)
  return !value.slice(0, firstSeparator).includes(':')
}

function decodeOnce(value: string): string | null {
  let decoded = ''
  for (let index = 0; index < value.length; index += 1) {
    if (value[index] !== '%') {
      decoded += value[index]
      continue
    }
    const pair = value.slice(index + 1, index + 3)
    if (!HEX_PAIR_RE.test(pair)) return null
    decoded += String.fromCharCode(Number.parseInt(pair, 16))
    index += 2
  }
  return decoded
}

function hasEncodedUrlSecret(value: string): boolean {
  let keptEnd = value.length
  for (const separator of ['?', '#']) {
    const index = value.indexOf(separator)
    if (index >= 0 && index < keptEnd) keptEnd = index
  }
  const kept = value.slice(0, keptEnd)
  if (!kept.includes('%')) return false

  let current = kept
  for (let pass = 0; pass < 3; pass += 1) {
    const decoded = decodeOnce(current)
    if (decoded === null) return true
    if (decoded === current) {
      JWT_RE.lastIndex = 0
      return decoded.includes('?') || decoded.includes('#') || decoded.includes('=') || JWT_RE.test(decoded)
    }
    current = decoded
  }
  // Üç geçişte hâlâ değişen gölge çözümlenememiş sayılır; sabit sınır DoS
  // yüzeyini kapatır ve gözlenen çift kodlamaya bir geçiş pay bırakır.
  return true
}

function lowercaseSchemeAndAuthority(value: string): string {
  const offset = value.startsWith('blob:') ? 5 : 0
  const parts = absoluteHttpParts(value.slice(offset))
  if (!parts) return value
  const authorityEnd = offset + parts.authorityEnd
  return value.slice(0, authorityEnd).toLowerCase() + value.slice(authorityEnd)
}

function cleanUrlQuery(value: string): string {
  const fragmentIndex = value.indexOf('#')
  const withoutFragment = fragmentIndex >= 0 ? value.slice(0, fragmentIndex) : value
  const queryIndex = withoutFragment.indexOf('?')
  const path = queryIndex >= 0 ? withoutFragment.slice(0, queryIndex) : withoutFragment
  const query = queryIndex >= 0 ? withoutFragment.slice(queryIndex + 1) : null
  const cleaned = query !== null && query.length > 0 ? `${path}?[query]` : path
  return lowercaseSchemeAndAuthority(cleaned)
}

function redactEmbeddedUrlQueries(value: string, kind: RedactionValueKind): string {
  if (kind !== 'text') return value
  return value.replace(EMBEDDED_URL_RE, (_match, boundary: string, candidate: string) => {
    if (!isAcceptableUrl(candidate) || hasEncodedUrlSecret(candidate)) return boundary + '[url]'
    return boundary + cleanUrlQuery(candidate)
  })
}

/**
 * TEK yürütme dizisi. Kayıtlar ELLE nesne olarak yazılmaz.
 *
 * Kimlik alanı yazım sözleşmesi (pazarlık edilemez — verify-redaction-drift-guard.sh
 * mutasyonlarını bu çapaya göre uyguluyor): `id: 'email',` biçiminde, tek tırnak.
 *
 * Sıra (pazarlık edilemez, yapısal adımlar önce): unicode-normalize →
 * url-format-gate → url-encoded-secret → url-query → embedded-url-query → auth-header → jwt →
 * key-value-secret → order-id → card → email → phone.
 */
export const REDACTION_RULES: readonly RedactionRule[] = [
  structuralRule({
    id: 'unicode-normalize',
    label: '[normalize]',
    apply: (value) => value.normalize('NFKC').replace(INVISIBLE_RE, ''),
  }),
  structuralRule({
    id: 'url-format-gate',
    label: '[url]',
    apply: (value, kind) => {
      if (kind !== 'url' || value === '') return value
      // `data:` satır içi içeriktir; gövde sızıntısını önlemek için bilinçli
      // teşhis kaybı pahasına URL olarak kabul edilmez.
      return isAcceptableUrl(value) ? value : '[url]'
    },
  }),
  structuralRule({
    id: 'url-encoded-secret',
    label: '[url]',
    apply: (value, kind) => (kind === 'url' && hasEncodedUrlSecret(value) ? '[url]' : value),
  }),
  structuralRule({
    id: 'url-query',
    label: '[query]',
    apply: (value, kind) => (kind === 'url' ? cleanUrlQuery(value) : value),
  }),
  structuralRule({
    id: 'embedded-url-query',
    label: '[query]',
    apply: redactEmbeddedUrlQueries,
  }),
  textRule({
    id: 'auth-header',
    label: '[auth-header]',
    pattern: AUTH_HEADER_RE,
    replace: '[auth-header]',
  }),
  textRule({
    id: 'jwt',
    label: '[token]',
    pattern: JWT_RE,
    replace: '[token]',
  }),
  textRule({
    id: 'key-value-secret',
    label: '[token]',
    pattern: KEY_VALUE_SECRET_RE,
    replace: replaceKeyValueSecret,
  }),
  textRule({
    id: 'order-id',
    label: '[order-id]',
    pattern: ORDER_RE,
    replace: '[order-id]',
  }),
  textRule({
    id: 'card',
    label: '[card]',
    pattern: CARD_RE,
    replace: (match) => replaceCard(match),
  }),
  textRule({
    id: 'email',
    label: '[email]',
    // Geriye dönük bakış (lookbehind) KULLANILMAZ — ES2020 hedefi, Safari <16.4
    // altında modül yüklenirken SyntaxError atıp tüm paketi düşürür.
    pattern: EMAIL_RE,
    replace: '[email]',
  }),
  textRule({
    id: 'phone',
    label: '[phone]',
    pattern: PHONE_CANDIDATE_RE,
    replace: replacePhone,
  }),
]

/**
 * `REDACTION_RULES`'tan TÜRETİLİR — paralel, elle yazılmış ikinci bir liste
 * TUTULMAZ. Kural düzeyi kilidinin (`RULE_SET_DIGEST`) girdisi budur.
 */
export const REDACTION_RULE_DESCRIPTORS: readonly { id: string; label: string }[] =
  REDACTION_RULES.map((rule) => ({ id: rule.id, label: rule.label }))

/**
 * Boru hattı dizinin kendisidir: `redactValue` bu dizi üzerinde SAF bir
 * katlamadır, içinde İKİNCİ bir dönüşüm YOKTUR (cycle-3 HIGH #1).
 */
export function redactValue(value: string, kind: RedactionValueKind): string {
  return REDACTION_RULES.reduce((acc, rule) => rule.apply(acc, kind), value)
}

/** `redactValue(text, 'text')` çağıran ince sarmalayıcı — kendi içinde dönüşüm yapmaz. */
export function redactText(text: string): string {
  return redactValue(text, 'text')
}

function redactionKindForKey(key: string): RedactionValueKind {
  return key === 'url' || key === 'referrer' ? 'url' : 'text'
}

function redactAny(value: unknown, kind: RedactionValueKind): unknown {
  if (typeof value === 'string') {
    return redactValue(value, kind)
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactAny(item, kind))
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
      result[key] = redactAny(inner, redactionKindForKey(key))
    }
    return result
  }
  return value
}

/**
 * Yalnız `REDACTED_METADATA_KEYS` anahtarlarını dolaşır (özyinelemeli); YENİ
 * bir nesne döndürür, girdiyi yerinde değiştirmez. Diğer tüm anahtarlar
 * (summary, description, email, contact, screenshot dahil) referansıyla
 * aynen aktarılır — D-11 dışlaması burada uygulanır.
 */
export function redactFeedbackMetadata<T extends Record<string, unknown>>(metadata: T): T {
  const result: Record<string, unknown> = { ...metadata }
  for (const key of REDACTED_METADATA_KEYS) {
    if (key in result) {
      result[key] = redactAny(result[key], 'text')
    }
  }
  return result as T
}
